import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table2, Database, ChevronLeft, Plus, Trash2, PanelLeftClose, PanelLeft, MoreVertical } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@shadcn-in-nx/ui/dropdown-menu";
import { Button } from "@shadcn-in-nx/ui/button";
import { cn } from "@shadcn-in-nx/lib/utils";
import { useTranslation } from "react-i18next";
import { useTableData } from "../context/TableDataContext";

function TablesSidebar({ onAddTable, onDeleteTable, isAdmin, isCollapsed, onToggleCollapse }) {
    const { t } = useTranslation();
    const { databaseId, tableId } = useParams();
    const navigate = useNavigate();
    const { databaseInfo, tablesList, isLoadingDatabase: isLoading } = useTableData();

    const currentDatabase = React.useMemo(() => {
        if (!databaseInfo) return null;
        return { ...databaseInfo, tables: tablesList };
    }, [databaseInfo, tablesList]);

    const handleTableClick = (tableIdToNavigate) => {
        // Keep current sidebar state when navigating between tables
        navigate(`/databases/${databaseId}/tables/${tableIdToNavigate}`, {
            state: { sidebarOpen: !isCollapsed }
        });
    };

    // Collapsed state - show only icons
    if (isCollapsed) {
        return (
            <div className="w-12 min-w-[48px] shrink-0 bg-gray-50 border-r border-border flex flex-col h-full">
                <div className="p-2 border-b border-border">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={onToggleCollapse}
                        title="Expand sidebar"
                    >
                        <PanelLeft className="w-4 h-4" />
                    </Button>
                </div>
                <div className="p-2 border-b border-border">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={() => navigate("/databases")}
                        title={t("databases.backToDatabases")}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-1">
                    {currentDatabase?.tables?.map((table) => (
                        <Button
                            key={table.id}
                            variant="ghost"
                            size="sm"
                            className={cn(
                                "w-8 h-8 p-0 mb-1",
                                table.id === tableId && "bg-primary/10 text-primary"
                            )}
                            onClick={() => handleTableClick(table.id)}
                            title={table.name}
                        >
                            <Table2 className="w-4 h-4" />
                        </Button>
                    ))}
                </div>
                {onAddTable && (
                    <div className="p-2 border-t border-border">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={onAddTable}
                            title={t("databases.addTable")}
                        >
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    // Expanded state
    return (
        <div className="w-64 min-w-[256px] shrink-0 bg-gray-50 border-r border-border flex flex-col h-full">
            {/* Header with collapse button */}
            <div className="p-2 border-b border-border flex items-center justify-between">
                <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start gap-2 text-muted-foreground hover:text-foreground"
                    onClick={() => navigate("/databases")}
                >
                    <ChevronLeft className="w-4 h-4" />
                    {t("databases.backToDatabases")}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 text-muted-foreground hover:text-foreground"
                    onClick={onToggleCollapse}
                    title="Collapse sidebar"
                >
                    <PanelLeftClose className="w-4 h-4" />
                </Button>
            </div>

            {/* Database Info */}
            <div className="p-3 border-b border-border">
                <div className="flex items-center gap-2 text-sm font-medium">
                    <Database className="w-4 h-4 text-muted-foreground" />
                    <span className="truncate">
                        {currentDatabase?.name || "Loading..."}
                    </span>
                </div>
                {currentDatabase?.description && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                        {currentDatabase.description}
                    </p>
                )}
            </div>

            {/* Tables List */}
            <div className="flex-1 overflow-y-auto p-2">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1 uppercase tracking-wide">
                    {t("databases.tables")}
                </div>
                {isLoading ? (
                    <div className="p-2 text-sm text-muted-foreground">
                        Loading...
                    </div>
                ) : currentDatabase?.tables?.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                        {t("databases.noTables")}
                    </div>
                ) : (
                    <div className="space-y-0.5">
                        {currentDatabase?.tables?.map((table) => (
                            <div
                                key={table.id}
                                className={cn(
                                    "group flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer transition-colors",
                                    table.id === tableId
                                        ? "bg-primary/10 text-primary"
                                        : "hover:bg-gray-100 text-foreground"
                                )}
                                onClick={() => handleTableClick(table.id)}
                            >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <Table2 className="w-4 h-4 flex-shrink-0" />
                                    <span className="text-sm truncate">
                                        {table.name}
                                    </span>
                                </div>
                                {isAdmin && onDeleteTable && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <MoreVertical className="w-3 h-3" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteTable(table);
                                                }}
                                                className="text-red-600 focus:text-red-600"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                {t("common.delete")}
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Table Button */}
            {onAddTable && (
                <div className="p-2 border-t border-border">
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start gap-2"
                        onClick={onAddTable}
                    >
                        <Plus className="w-4 h-4" />
                        {t("databases.addTable")}
                    </Button>
                </div>
            )}
        </div>
    );
}

export default TablesSidebar;
